import { hasPassword } from "../lib/mask-connection-string";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import React, { useState, useEffect, useId } from "react";
import { useNavigate } from "react-router-dom";
import {
  DatasourceDto,
  DatasourceDtoTypeEnum,
} from "../lib/generated/api-client";
import { getApi } from "../lib/get-api";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";
import { AutocompleteSelect } from "./AutocompleteSelect";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { startSheetsSignIn } from "../lib/sheets-oauth";

const ErrorBox: React.FC<{ error: any }> = ({ error }) => {
  const messages =
    typeof error.message === "string"
      ? [error.message]
      : Array.isArray(error.message)
        ? error.message
        : [];
  return (
    // #dc2626 on the red 10% tint is 4.23:1. The surface background is 4.83:1.
    <div
      role="alert"
      className="bg-od-surface border border-od-danger text-od-danger px-6 py-4 rounded-xl relative mb-6"
    >
      <p className="font-bold">
        The request failed (error {error.statusCode}).
      </p>
      {messages.length > 0 && (
        <ul className="mt-3 space-y-1">
          {messages.map((msg: string, index: number) => (
            <li key={index}>{msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface DataSourceFormProps {
  onSubmit?: (datasource: DatasourceDto) => void;
  onCancel?: () => void;
  initialData?: DatasourceDto;
}

/** Schemes we can actually connect. `postgres://` is not POSTGRES. */
const URI_TYPE: Record<string, DatasourceDtoTypeEnum> = {
  postgres: DatasourceDtoTypeEnum.POSTGRESQL,
  postgresql: DatasourceDtoTypeEnum.POSTGRESQL,
  mysql: DatasourceDtoTypeEnum.MYSQL,
  mongodb: DatasourceDtoTypeEnum.MONGODB,
  "mongodb+srv": DatasourceDtoTypeEnum.MONGODB,
};

function typeFromUri(raw: string): DatasourceDtoTypeEnum | undefined {
  try {
    const scheme = new URL(raw).protocol.replace(/:$/, "").toLowerCase();
    return URI_TYPE[scheme];
  } catch {
    return undefined;
  }
}

/**
 * Database type picker options; order is the order shown in the dropdown.
 * MySQL and MongoDB stay in URI_TYPE and the API client; U7 hides them from
 * the picker until they are live (MOSA-438 safe default).
 */
const TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "PostgreSQL", value: DatasourceDtoTypeEnum.POSTGRESQL },
  { label: "Google Sheets", value: DatasourceDtoTypeEnum.GOOGLE_SHEETS },
];

function nameFromUri(raw: string): string | undefined {
  try {
    const url = new URL(raw);
    const host = url.hostname;
    const db = decodeURIComponent(url.pathname.replace(/^\//, "")).split("/")[0];
    if (db && host) return `${db} @ ${host}`;
    return db || host || undefined;
  } catch {
    return undefined;
  }
}

const DataSourceForm: React.FC<DataSourceFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
}) => {
  const [databaseType, setDatabaseType] = useState<DatasourceDtoTypeEnum>(
    initialData?.type || DatasourceDtoTypeEnum.POSTGRESQL
  );
  const [datasourceName, setDatasourceName] = useState(
    initialData?.name || ""
  );
  const [autoName, setAutoName] = useState<string>("");
  const [csRevealed, setCsRevealed] = useState(false);
  const [csFocused, setCsFocused] = useState(false);
  type TestStatus = "idle" | "testing" | "ok" | "fail";
  const [testStatus, setTestStatus] = useState<TestStatus>("idle");
  const [testMessage, setTestMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [connectionString, setConnectionString] = useState<string>(
    initialData?.connectionString || ""
  );

  // Obscured unless the user is actively editing it or asked to see it.
  const csObscured = !csFocused && !csRevealed && hasPassword(connectionString);
  const { fetchDatasources } = useDatasources();
  const { token, user, guest } = useAuth();
  // Beta sign-up gate: guests can open the demo only. Connecting a Google
  // Sheet needs a signed-up account. The Postgres guest path is unchanged.
  const isGuest = Boolean(guest) && !user?.email;
  const [showSignUpGate, setShowSignUpGate] = useState(false);
  // MOSA-146: Google sign-in replaces the service-account share option for new
  // connections. No OAuth config is a normal state, not an error.
  const [sheetsSignIn, setSheetsSignIn] = useState<{
    status: "idle" | "starting" | "not_configured" | "error" | "ready";
    message?: string;
  }>({ status: "idle" });
  const navigate = useNavigate();
  const databaseTypeId = useId();
  const nameId = useId();
  const connectionStringId = useId();
  const sheetsLinkId = useId();

  const api = getApi({ token });

  useEffect(() => {
    if (
      initialData?.connectionString &&
      initialData.type !== DatasourceDtoTypeEnum.GOOGLE_SHEETS
    ) {
      handleConnectionStringChange({
        target: { value: initialData.connectionString },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const handleTestConnection = async () => {
    if (
      isGuest &&
      databaseType === DatasourceDtoTypeEnum.GOOGLE_SHEETS
    ) {
      setShowSignUpGate(true);
      return;
    }
    setTestStatus("testing");
    setTestMessage("");
    setError(null);
    try {
      const newDatabase: DatasourceDto = {
        name:
          datasourceName.trim() ||
          nameFromUri(connectionString) ||
          "Database",
        type: databaseType,
        connectionString: connectionString,
      };
      await api.datasources.testDatasource(newDatabase);
      setTestStatus("ok");
      setTestMessage("Connection successful");
    } catch (err: any) {
      setError(err?.error || err);
      setTestStatus("fail");
      setTestMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to connect with these credentials",
      );
    }
  };

  // Any edit to the connection string or datasource details invalidates a prior
  // test result. The button goes back to "Test connection" instead of showing
  // a stale success badge.
  useEffect(() => {
    if (testStatus !== "idle") {
      setTestStatus("idle");
      setTestMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionString, databaseType, datasourceName]);

  const handleCreateDatabase = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      isGuest &&
      databaseType === DatasourceDtoTypeEnum.GOOGLE_SHEETS
    ) {
      setShowSignUpGate(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const newDatabase: DatasourceDto = {
        name:
          datasourceName.trim() ||
          nameFromUri(connectionString) ||
          "Database",
        type: databaseType,
        connectionString: connectionString,
      };

      if (initialData?.id) {
        newDatabase.id = initialData.id;
        await api.datasources.updateDatasource(initialData.id, newDatabase);
      } else {
        await api.datasources.createDatasource(newDatabase);
      }

      onSubmit?.(newDatabase);
      fetchDatasources();
    } catch (error: any) {
      console.error("Error creating database:", error);
      if (error.response && error.response.data) {
        setError(error.response.data);
      } else {
        setError({
          message: ["Error creating database"],
          error: "Unknown Error",
          statusCode: 500,
        });
      }
      setTestStatus("fail");
      setTestMessage("Couldn't save this connection. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionStringChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newConnectionString = e.target.value;
    setConnectionString(newConnectionString);

    if (databaseType === DatasourceDtoTypeEnum.GOOGLE_SHEETS) {
      return;
    }

    const inferred = typeFromUri(newConnectionString);
    if (inferred) setDatabaseType(inferred);

    const auto = nameFromUri(newConnectionString);
    if (
      auto &&
      (datasourceName === "" ||
        datasourceName === "Test" ||
        datasourceName === autoName)
    ) {
      setDatasourceName(auto);
      setAutoName(auto);
    }
  };

  return (
    <div>
      {loading && <p className="text-od-muted mb-4">Loading…</p>}
      {error && <ErrorBox error={error} />}

      <form onSubmit={handleCreateDatabase} className="text-left space-y-4">
        <div className="space-y-4">
          <div>
            <label
              htmlFor={databaseTypeId}
              className="block text-od-text mb-1 text-sm font-medium"
            >
              Database type
            </label>
            <AutocompleteSelect
              id={databaseTypeId}
              options={TYPE_OPTIONS}
              disableClearable
              value={databaseType}
              // Not clearable and not multiple, so this is always one of the
              // TYPE_OPTIONS values.
              onChange={(value) => {
                const next = value as DatasourceDtoTypeEnum;
                if (
                  next === DatasourceDtoTypeEnum.GOOGLE_SHEETS &&
                  isGuest
                ) {
                  setShowSignUpGate(true);
                  return;
                }
                setShowSignUpGate(false);
                setDatabaseType(next);
              }}
            />
          </div>

          {showSignUpGate && (
            <div
              className="mb-4 rounded-md border border-od-border bg-od-surface-2 p-3"
              role="alert"
              data-testid="sheets-signup-gate"
            >
              <h3 className="text-sm font-semibold text-od-text mb-1">
                Sign up to connect a Google Sheet
              </h3>
              <p className="text-sm text-od-text">
                Guests can use the demo. To connect a Google Sheet, create an
                account.
              </p>
              <Button
                variant="primary" size="sm" className="mt-2"
                onClick={() => navigate("/auth")}
              >
                Sign up
              </Button>
            </div>
          )}

          {databaseType === DatasourceDtoTypeEnum.GOOGLE_SHEETS &&
            !showSignUpGate && (
              <div className="mb-4 rounded-md border border-od-border bg-od-surface-2 p-3">
                <h3 className="text-sm font-semibold text-od-text mb-1">
                  Sign in with Google
                </h3>
                <p className="text-sm text-od-text">
                  Sign in with Google, then pick the sheet to use. OnlyDash
                  gets access to the file you pick, nothing else in your Drive.
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-2"
                  data-testid="sheets-signin-button"
                  disabled={sheetsSignIn.status === "starting"}
                  onClick={async () => {
                    setSheetsSignIn({ status: "starting" });
                    const result = await startSheetsSignIn(token);
                    if (result.status === "ready") {
                      window.location.assign(result.url);
                      return;
                    }
                    if (result.status === "signup_required") {
                      setShowSignUpGate(true);
                      setSheetsSignIn({ status: "idle" });
                      return;
                    }
                    setSheetsSignIn({
                      status:
                        result.status === "not_configured"
                          ? "not_configured"
                          : "error",
                      message: result.message,
                    });
                  }}
                >
                  {sheetsSignIn.status === "starting"
                    ? "Opening Google..."
                    : "Sign in with Google"}
                </Button>
                {sheetsSignIn.status === "not_configured" && (
                  <p
                    className="mt-2 text-sm text-od-muted"
                    role="status"
                    data-testid="sheets-not-configured"
                  >
                    Google sign-in is not configured yet.
                  </p>
                )}
                {sheetsSignIn.status === "error" && (
                  <p className="mt-2 text-sm text-od-danger" role="alert">
                    {sheetsSignIn.message}
                  </p>
                )}
              </div>
            )}

          {databaseType !== DatasourceDtoTypeEnum.GOOGLE_SHEETS && (
            <>
              <div>
                <label
                  htmlFor={nameId}
                  className="block text-od-text mb-1 text-sm font-medium"
                >
                  Name
                </label>
                <Input
                size="sm"
                  id={nameId}
                  type="text"
                  placeholder="Enter a name"
                  value={datasourceName}
                  onChange={(e) => setDatasourceName(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor={connectionStringId}
                  className="block text-od-text mb-1 text-sm font-medium"
                >
                  Connection string
                </label>
                {/* Obscured whenever it is not being edited, so the password
                    cannot be read off a screenshot or a shared screen.
                    Keep type=text: type=password makes password managers save
                    the whole URI. Disc masking is CSS text-security instead. */}
                <div className="relative">
                  <Input
                size="sm"
                    id={connectionStringId}
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    className={`pr-12 sm:pr-10 ${
                      csObscured ? "[-webkit-text-security:disc] [text-security:disc]" : ""
                    }`}
                    placeholder="postgres://user:pass@host:5432/db"
                    value={connectionString}
                    onFocus={() => setCsFocused(true)}
                    onBlur={() => setCsFocused(false)}
                    onChange={handleConnectionStringChange}
                  />
                  {/* onClick owns the toggle, so mouse, touch, Enter and Space
                      all take one path. onMouseDown only preventDefaults, which
                      stops the press from blurring the input and re-masking it
                      on the way in; it must not toggle, or a keyboard press
                      would be dead and a press-released-off the button would
                      flip it with no click to match. */}
                  {hasPassword(connectionString) && (
                    <Button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setCsRevealed((shown) => !shown)}
                      aria-pressed={csRevealed}
                      variant="ghost" size="icon" className="absolute right-0 top-1/2 -translate-y-1/2 hover:bg-transparent sm:right-1"
                      aria-label={
                        csRevealed ? "Hide connection string" : "Show connection string"
                      }
                      title={
                        csRevealed ? "Hide connection string" : "Show connection string"
                      }
                    >
                      {csRevealed ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                      )}
                    </Button>
                  )}
                </div>
                <p className="mt-1 text-sm text-od-muted">
                  Use a string like postgres://user:pass@host:5432/db. OnlyDash
                  keeps the password in this form. Click Test connection to
                  check it.
                </p>
              </div>
            </>
          )}

          {databaseType === DatasourceDtoTypeEnum.GOOGLE_SHEETS && (
            <div>
              <label
                htmlFor={nameId}
                className="block text-od-text mb-1 text-sm font-medium"
              >
                Name
              </label>
              <Input
                size="sm"
                id={nameId}
                type="text"
                placeholder="Give this panel a name"
                value={datasourceName}
                onChange={(e) => setDatasourceName(e.target.value)}
              />
            </div>
          )}

          {databaseType === DatasourceDtoTypeEnum.GOOGLE_SHEETS && (
            <div>
              <label
                htmlFor={sheetsLinkId}
                className="block text-od-text mb-1 text-sm font-medium"
              >
                Google Sheet link
              </label>
              <Input
                size="sm"
                id={sheetsLinkId}
                type="text"
                placeholder="Enter Google Sheets link"
                value={connectionString}
                onChange={handleConnectionStringChange}
              />
            </div>
          )}
        </div>

        <div
          className={`flex items-center gap-3 rounded-md border px-3 py-2 transition-colors duration-200 ${
            testStatus === "ok"
              ? "border-od-success bg-od-surface-2"
              : testStatus === "fail"
              ? "border-od-danger bg-od-surface"
              : "border-od-border bg-od-surface/60"
          }`}
        >
          <Button
            onClick={handleTestConnection}
            disabled={testStatus === "testing" || !connectionString}
            variant="secondary"
            size="sm"
            className={`gap-2 text-sm ${
              testStatus === "ok"
                ? "border-od-success bg-transparent text-od-success hover:bg-transparent"
                : testStatus === "fail"
                ? "border-od-danger bg-od-surface text-od-danger hover:bg-od-surface"
                : "bg-od-surface hover:border-od-primary"
            }`}
            aria-busy={testStatus === "testing"}
          >
            {testStatus === "testing" && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                className="animate-spin"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="42 16"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {testStatus === "ok" && (
              <span aria-hidden="true" className="text-base leading-none">
                ✓
              </span>
            )}
            {testStatus === "fail" && (
              <span aria-hidden="true" className="text-base leading-none">
                ✗
              </span>
            )}
            {testStatus === "testing"
              ? "Testing…"
              : testStatus === "ok"
              ? "Reachable"
              : testStatus === "fail"
              ? "Try again"
              : "Test connection"}
          </Button>
          <span
            className={`text-sm flex-1 truncate ${
              testStatus === "ok"
                ? "text-od-success"
                : testStatus === "fail"
                ? "text-od-danger"
                : "text-od-muted"
            }`}
            role="status"
          >
            {testStatus === "testing"
              ? "Reaching out to your database…"
              : testStatus === "ok"
              ? testMessage
              : testStatus === "fail"
              ? testMessage
              : "Test before connecting to make sure credentials are right."}
          </span>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="secondary" className="px-4 text-sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            variant="primary" className="px-6 text-sm"
          >
            {loading
              ? "Saving…"
              : initialData?.id
              ? "Update"
              : "Connect"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DataSourceForm;
